import React, { useState, useCallback, useEffect, useRef } from 'react';
import { GoogleMap, LoadScript } from '@react-google-maps/api';

const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || 'YOUR_API_KEY_HERE';

const mapContainerStyle = {
  width: '100%',
  height: '400px',
  borderRadius: 'var(--radius-lg)',
  border: '2px solid var(--gray-200)'
};

const defaultCenter = {
  lat: 28.6139,  // Default: New Delhi
  lng: 77.2090
};

// Libraries to load
const libraries = ['places'];

function AddressMapPicker({ initialPosition, onLocationSelect }) {
  const [markerPosition, setMarkerPosition] = useState(initialPosition || defaultCenter);
  const [isLoading, setIsLoading] = useState(false);
  const [map, setMap] = useState(null);
  const markerRef = useRef(null);
  const isProgrammaticMoveRef = useRef(false);

  // Update marker when initialPosition changes
  useEffect(() => {
    if (initialPosition && initialPosition.lat && initialPosition.lng) {
      setMarkerPosition(initialPosition);
    }
  }, [initialPosition]);

  // Handle map idle (when user stops dragging/panning the map)
  const onMapIdle = useCallback(() => {
    if (map && !isProgrammaticMoveRef.current) {
      const center = map.getCenter();
      const lat = center.lat();
      const lng = center.lng();
      const newPos = { lat, lng };

      setMarkerPosition(newPos);
      setIsLoading(true);

      // Reverse geocode the center position
      const geocoder = new window.google.maps.Geocoder();
      geocoder.geocode({ location: newPos }, (results, status) => {
        setIsLoading(false);

        if (status === 'OK' && results[0]) {
          const addressComponents = results[0].address_components;
          const formattedAddress = results[0].formatted_address;

          let street = '';
          let city = '';
          let state = '';
          let zipCode = '';

          addressComponents.forEach(component => {
            const types = component.types;
            if (types.includes('street_number')) street = component.long_name + ' ';
            if (types.includes('route')) street += component.long_name;
            if (types.includes('locality')) city = component.long_name;
            if (types.includes('administrative_area_level_1')) state = component.short_name;
            if (types.includes('postal_code')) zipCode = component.long_name;
          });

          if (!street && formattedAddress) {
            street = formattedAddress.split(',')[0];
          }

          onLocationSelect({
            coordinates: newPos,
            street: street || '',
            city: city || '',
            state: state || '',
            zipCode: zipCode || '',
            formattedAddress: formattedAddress
          });
        }
      });
    }

    // Reset the flag after idle completes
    if (isProgrammaticMoveRef.current) {
      isProgrammaticMoveRef.current = false;
    }
  }, [map, onLocationSelect]);

  // Handle map load and create center-fixed marker
  const onMapLoad = useCallback((map) => {
    setMap(map);

    // Set initial center
    const initialPos = initialPosition || defaultCenter;
    map.setCenter(initialPos);

    // Create a custom marker using native Google Maps API
    if (window.google && window.google.maps) {
      const marker = new window.google.maps.Marker({
        position: initialPos,
        map: map,
        draggable: false, // Marker is fixed, map moves instead
        title: 'Drag the map to select your address',
        icon: {
          path: window.google.maps.SymbolPath.CIRCLE,
          scale: 10,
          fillColor: '#FF6B6B',
          fillOpacity: 1,
          strokeColor: '#FFFFFF',
          strokeWeight: 3,
        },
        // Keep marker centered by setting position based on map center
        optimized: false
      });

      markerRef.current = marker;
    }
  }, [initialPosition]);

  // Keep marker at center of map when map moves
  useEffect(() => {
    if (map && markerRef.current) {
      const centerChangedListener = map.addListener('center_changed', () => {
        const center = map.getCenter();
        if (markerRef.current) {
          markerRef.current.setPosition(center);
        }
      });

      return () => {
        window.google.maps.event.removeListener(centerChangedListener);
      };
    }
  }, [map]);

  // Cleanup marker on unmount
  useEffect(() => {
    return () => {
      if (markerRef.current) {
        markerRef.current.setMap(null);
        markerRef.current = null;
      }
    };
  }, []);

  // Center map on current location
  const centerOnCurrentLocation = useCallback(() => {
    if (!navigator.geolocation) {
      alert('Geolocation is not supported by your browser');
      return;
    }

    setIsLoading(true);
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const lat = position.coords.latitude;
        const lng = position.coords.longitude;
        const newPos = { lat, lng };

        if (map) {
          // Set flag to prevent onMapIdle from triggering during programmatic move
          isProgrammaticMoveRef.current = true;

          // Pan the map to current location
          map.panTo(newPos);

          // Manually trigger geocoding after pan completes
          // Use a small delay to ensure pan animation starts
          setTimeout(() => {
            const geocoder = new window.google.maps.Geocoder();
            geocoder.geocode({ location: newPos }, (results, status) => {
              setIsLoading(false);

              if (status === 'OK' && results[0]) {
                const addressComponents = results[0].address_components;
                const formattedAddress = results[0].formatted_address;

                let street = '', city = '', state = '', zipCode = '';

                addressComponents.forEach(component => {
                  const types = component.types;
                  if (types.includes('street_number')) street = component.long_name + ' ';
                  if (types.includes('route')) street += component.long_name;
                  if (types.includes('locality')) city = component.long_name;
                  if (types.includes('administrative_area_level_1')) state = component.short_name;
                  if (types.includes('postal_code')) zipCode = component.long_name;
                });

                if (!street && formattedAddress) {
                  street = formattedAddress.split(',')[0];
                }

                setMarkerPosition(newPos);
                onLocationSelect({
                  coordinates: newPos,
                  street: street || '',
                  city: city || '',
                  state: state || '',
                  zipCode: zipCode || '',
                  formattedAddress: formattedAddress
                });
              }
            });
          }, 500);
        }
      },
      (error) => {
        console.error('Error getting location:', error);
        alert('Could not get your location. Please enable location permissions.');
        setIsLoading(false);
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 0
      }
    );
  }, [map, onLocationSelect]);

  return (
    <div style={{ marginBottom: 'var(--space-lg)' }}>
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: 'var(--space-md)'
      }}>
        <label style={{
          fontWeight: '600',
          color: 'var(--gray-900)',
          fontSize: 'var(--font-size-base)'
        }}>
          🗺️ Select Location on Map
        </label>
        <button
          type="button"
          onClick={centerOnCurrentLocation}
          className="btn btn-secondary"
          style={{
            padding: 'var(--space-sm) var(--space-md)',
            fontSize: 'var(--font-size-sm)'
          }}
        >
          📍 My Location
        </button>
      </div>

      <p style={{
        color: 'var(--gray-600)',
        fontSize: 'var(--font-size-sm)',
        marginBottom: 'var(--space-md)'
      }}>
        {isLoading ? '🔄 Loading address...' : '🗺️ Drag the map to position the marker at your desired location'}
      </p>

      <LoadScript
        googleMapsApiKey={GOOGLE_MAPS_API_KEY}
        libraries={libraries}
        loadingElement={<div style={{ textAlign: 'center', padding: '20px' }}>🗺️ Loading map...</div>}
      >
        <GoogleMap
          mapContainerStyle={mapContainerStyle}
          zoom={15}
          onLoad={onMapLoad}
          onIdle={onMapIdle}
          options={{
            streetViewControl: false,
            mapTypeControl: false,
            fullscreenControl: true,
            zoomControl: true,
            gestureHandling: 'greedy',
          }}
        >
          {/* Marker is created programmatically and stays at center */}
        </GoogleMap>
      </LoadScript>

      <div style={{
        marginTop: 'var(--space-md)',
        padding: 'var(--space-md)',
        background: 'var(--gray-50)',
        borderRadius: 'var(--radius-md)',
        border: '1px solid var(--gray-200)'
      }}>
        <p style={{
          margin: 0,
          fontSize: 'var(--font-size-sm)',
          color: 'var(--gray-700)'
        }}>
          📌 <strong>Selected Coordinates:</strong> {markerPosition.lat.toFixed(6)}, {markerPosition.lng.toFixed(6)}
        </p>
      </div>
    </div>
  );
}

export default AddressMapPicker;
